import { create } from 'zustand';

/**
 * The Admin country the staff member chose — client-only, app-wide state, so
 * it lives in Zustand next to the session (see CLAUDE.md, State). It holds an
 * id only: which countries exist and which are allowed is server state from
 * `/admin/me`, and `useSelectedCountry` validates the id against it.
 *
 * The choice is remembered in localStorage (it is not a secret). Storage may be
 * unavailable (private mode, blocked site data); the selection then simply
 * lasts for the tab.
 */
const STORAGE_KEY = 'dc-admin.selected-country';

function readStoredId(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const id = raw === null ? NaN : Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function storeId(countryId: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(countryId));
  } catch {
    // Remembering is a convenience; the selection still applies to this tab.
  }
}

interface SelectedCountryState {
  countryId: number | null;
  select: (countryId: number) => void;
}

export const useSelectedCountryStore = create<SelectedCountryState>((set) => ({
  countryId: readStoredId(),
  select: (countryId) => {
    storeId(countryId);
    set({ countryId });
  },
}));
